// components/UnsubscribePage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Home, Book, UserX } from 'lucide-react';
import { submitUnsubscribeToAPI } from '../utils/api';
import { getEmailFromURL } from '../utils/urlHelpers';
import Header from './header';

const UnsubscribePage = () => {
  const navigate = useNavigate();
  const [unsubscribeEmail, setUnsubscribeEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isUnsubscribed, setIsUnsubscribed] = useState(false);
  const [error, setError] = useState('');

  // Safely prefill email from URL on component mount
  useEffect(() => {
    const emailFromURL = getEmailFromURL();
    if (emailFromURL) {
      setUnsubscribeEmail(emailFromURL);
    }
  }, []);

  const handleUnsubscribe = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!unsubscribeEmail || !unsubscribeEmail.includes('@')) {
      setError('Please enter a valid email address.');
      return;
    }

    setIsLoading(true);
    const result = await submitUnsubscribeToAPI(unsubscribeEmail);
    setIsLoading(false);
    
    if (result.success) {
      setIsUnsubscribed(true);
      console.log('Successfully unsubscribed:', result.data);
    } else {
      setError('Failed to unsubscribe. Please try again or contact support.');
      console.error('Unsubscribe failed:', result.error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-100">
      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-2xl shadow-lg p-8">
          {!isUnsubscribed ? (
            <>
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
                  <UserX className="h-8 w-8 text-red-600" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Unsubscribe</h2>
                <p className="text-lg text-gray-600">
                  We're sorry to see you go! Enter your email address below to unsubscribe from our monthly CPR quiz reminders.
                </p>
              </div>

              <form onSubmit={handleUnsubscribe} className="space-y-6">
                <div>
                  <label htmlFor="unsubscribe-email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="unsubscribe-email"
                    value={unsubscribeEmail}
                    onChange={(e) => setUnsubscribeEmail(e.target.value)}
                    placeholder="Enter your email address"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition-colors"
                    required
                    disabled={isLoading}
                  />
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-red-700 text-sm">{error}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isLoading}
                  className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${
                    isLoading
                      ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                      : 'bg-red-600 text-white hover:bg-red-700 focus:ring-2 focus:ring-red-500 focus:ring-offset-2'
                  }`}
                >
                  {isLoading ? (
                    <span className="inline-flex items-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-gray-200" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Processing...
                    </span>
                  ) : (
                    'Unsubscribe'
                  )}
                </button>
              </form>

              <div className="mt-6 text-center">
                <p className="text-sm text-gray-500">
                  Changed your mind? 
                  <button
                    onClick={() => navigate('/')}
                    className="ml-1 text-indigo-600 hover:text-indigo-700 font-medium"
                  >
                    Go back to subscribe
                  </button>
                </p>
              </div>
            </>
          ) : (
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Successfully Unsubscribed</h2>
              <p className="text-lg text-gray-600 mb-6">
                You have been successfully unsubscribed from our monthly CPR quiz reminders. We hope the knowledge you've gained will serve you well.
              </p>
              <div className="space-y-4">
                <button
                  onClick={() => navigate('/')}
                  className="inline-flex items-center px-6 py-3 border border-indigo-600 text-base font-medium rounded-lg text-indigo-600 bg-white hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  <Home className="mr-2 h-4 w-4" />
                  Return to Home
                </button>
              </div>
              <div className="mt-6">
                <p className="text-sm text-gray-500">
                  Want to stay updated? You can always 
                  <button
                    onClick={() => navigate('/')}
                    className="ml-1 text-indigo-600 hover:text-indigo-700 font-medium"
                  >
                    subscribe again
                  </button>
                </p>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default UnsubscribePage;